// 監査ログ閲覧(設計書 §3.5.6)
// 全文検索 + actor/category/severity 絞り込み + CSV エクスポート

import { useState } from 'react';
import { ApiClientError } from '../../api/client';
import {
  downloadAuditLogsCsv,
  useAuditLogs,
  type ActorType,
  type AuditCategory,
  type AuditLogEntry,
  type AuditLogsQuery,
  type AuditSeverity,
} from '../../api/admin/audit-logs';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';

const PER_PAGE = 30;

const ACTOR_OPTIONS: Array<{ value: ActorType | ''; label: string }> = [
  { value: '', label: 'すべて' },
  { value: 'admin', label: 'admin' },
  { value: 'performer', label: 'performer' },
  { value: 'system', label: 'system' },
  { value: 'agent', label: 'agent' },
];

const CATEGORY_OPTIONS: Array<{ value: AuditCategory | ''; label: string }> = [
  { value: '', label: 'すべて' },
  { value: 'auth', label: 'auth' },
  { value: 'config_change', label: 'config_change' },
  { value: 'approval', label: 'approval' },
  { value: 'session', label: 'session' },
  { value: 'system', label: 'system' },
  { value: 'admin_action', label: 'admin_action' },
];

const SEVERITY_OPTIONS: Array<{ value: AuditSeverity | ''; label: string }> = [
  { value: '', label: 'すべて' },
  { value: 'info', label: 'info' },
  { value: 'warning', label: 'warning' },
  { value: 'error', label: 'error' },
  { value: 'critical', label: 'critical' },
];

export function AdminAuditLogsPage() {
  const [page, setPage] = useState(1);
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [actor, setActor] = useState<ActorType | ''>('');
  const [category, setCategory] = useState<AuditCategory | ''>('');
  const [severity, setSeverity] = useState<AuditSeverity | ''>('');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AuditLogEntry | null>(null);

  const params: AuditLogsQuery = {
    page,
    per_page: PER_PAGE,
    ...(q ? { q } : {}),
    ...(actor ? { actor_type: actor } : {}),
    ...(category ? { category } : {}),
    ...(severity ? { severity } : {}),
  };
  const logsQuery = useAuditLogs(params);

  function applyFilter(setter: () => void) {
    setPage(1);
    setter();
  }

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    applyFilter(() => setQ(qDraft.trim()));
  }

  async function handleDownload() {
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadAuditLogsCsv({
        ...(q ? { q } : {}),
        ...(actor ? { actor_type: actor } : {}),
        ...(category ? { category } : {}),
        ...(severity ? { severity } : {}),
      });
    } catch (err) {
      setDownloadError(err instanceof Error ? err.message : 'CSV エクスポートに失敗しました');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <AdminLayout
      title="監査ログ"
      description="操作履歴の検索とエクスポート。"
      actions={
        <div style={{ minWidth: 160 }}>
          <Button variant="secondary" onClick={handleDownload} disabled={downloading}>
            {downloading ? 'ダウンロード中…' : 'CSV エクスポート'}
          </Button>
        </div>
      }
    >
      <Card>
        <form onSubmit={applySearch}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 12,
              alignItems: 'flex-end',
              marginBottom: 12,
            }}
          >
            <Input
              label="検索(action / message / actor_label / target_label に対して部分一致)"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              maxLength={200}
            />
            <div style={{ minWidth: 100, marginBottom: 16 }}>
              <Button
                type="submit"
                variant="secondary"
                style={{ padding: '9px 12px', fontSize: '11px' }}
              >
                検索
              </Button>
            </div>
          </div>
        </form>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <Select
            label="アクター"
            value={actor}
            onChange={(e) => applyFilter(() => setActor(e.target.value as ActorType | ''))}
          >
            {ACTOR_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Select
            label="カテゴリ"
            value={category}
            onChange={(e) => applyFilter(() => setCategory(e.target.value as AuditCategory | ''))}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <Select
            label="重要度"
            value={severity}
            onChange={(e) => applyFilter(() => setSeverity(e.target.value as AuditSeverity | ''))}
          >
            {SEVERITY_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      {downloadError && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>{downloadError}</p>
        </Card>
      )}

      {logsQuery.isLoading && !logsQuery.data && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>読み込み中…</p>
        </Card>
      )}

      {logsQuery.error && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-error-text)' }}>
            {logsQuery.error instanceof ApiClientError
              ? logsQuery.error.message
              : '取得に失敗しました'}
          </p>
        </Card>
      )}

      {logsQuery.data && logsQuery.data.data.length === 0 && (
        <Card>
          <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
            該当するログはありません。
          </p>
        </Card>
      )}

      {logsQuery.data && logsQuery.data.data.length > 0 && (
        <Card>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-tertiary)' }}>
                <Th>時刻</Th>
                <Th>重要度</Th>
                <Th>アクター</Th>
                <Th>アクション</Th>
                <Th>対象</Th>
                <Th>メッセージ</Th>
              </tr>
            </thead>
            <tbody>
              {logsQuery.data.data.map((entry) => (
                <LogRow key={entry.id} entry={entry} onSelect={() => setDetail(entry)} />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {logsQuery.data && logsQuery.data.pagination.total_pages > 1 && (
        <Card>
          <Pagination
            page={logsQuery.data.pagination.page}
            totalPages={logsQuery.data.pagination.total_pages}
            total={logsQuery.data.pagination.total}
            onChange={setPage}
            disabled={logsQuery.isFetching}
          />
        </Card>
      )}

      <Modal
        open={detail !== null}
        title={`監査ログ #${detail?.id ?? ''}`}
        onClose={() => setDetail(null)}
        width={680}
      >
        {detail && <LogDetail entry={detail} />}
      </Modal>
    </AdminLayout>
  );
}

function LogRow({
  entry,
  onSelect,
}: {
  entry: AuditLogEntry;
  onSelect: () => void;
}) {
  return (
    <tr
      style={{ borderTop: 'var(--border-default)', cursor: 'pointer' }}
      onClick={onSelect}
    >
      <Td style={{ whiteSpace: 'nowrap', color: 'var(--color-text-secondary)' }}>
        {formatDateTime(entry.occurred_at)}
      </Td>
      <Td>
        <SeverityBadge severity={entry.severity} />
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>
        {entry.actor_label}
        <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>
          {entry.actor_type}
        </div>
      </Td>
      <Td style={{ color: 'var(--color-text-primary)', fontFamily: 'monospace', fontSize: '10.5px' }}>
        {entry.action}
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>
        {entry.target_label ?? '—'}
        {entry.target_type && (
          <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>
            {entry.target_type}
          </div>
        )}
      </Td>
      <Td style={{ color: 'var(--color-text-secondary)' }}>{entry.message}</Td>
    </tr>
  );
}

function LogDetail({ entry }: { entry: AuditLogEntry }) {
  return (
    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 8, fontSize: '11px' }}>
      <Term>時刻</Term>
      <Detail>{formatDateTime(entry.occurred_at)}</Detail>
      <Term>重要度</Term>
      <Detail>{entry.severity}</Detail>
      <Term>カテゴリ</Term>
      <Detail>{entry.category}</Detail>
      <Term>アクター</Term>
      <Detail>
        {entry.actor_label} ({entry.actor_type}
        {entry.actor_id ? `, id=${entry.actor_id}` : ''})
      </Detail>
      <Term>アクション</Term>
      <Detail style={{ fontFamily: 'monospace' }}>{entry.action}</Detail>
      <Term>対象</Term>
      <Detail>
        {entry.target_label ?? '—'}
        {entry.target_type ? ` (${entry.target_type}, id=${entry.target_id ?? '—'})` : ''}
      </Detail>
      <Term>IP</Term>
      <Detail>{entry.ip ?? '—'}</Detail>
      <Term>UA</Term>
      <Detail style={{ wordBreak: 'break-all' }}>{entry.user_agent ?? '—'}</Detail>
      <Term>メッセージ</Term>
      <Detail>{entry.message}</Detail>
      <Term>詳細</Term>
      <Detail>
        {entry.details ? (
          <pre
            style={{
              margin: 0,
              padding: 8,
              background: 'rgba(0,0,0,0.3)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '10.5px',
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(entry.details, null, 2)}
          </pre>
        ) : (
          '—'
        )}
      </Detail>
    </dl>
  );
}

function SeverityBadge({ severity }: { severity: AuditSeverity }) {
  const colors: Record<AuditSeverity, { fg: string; bg: string }> = {
    info: { fg: 'var(--color-text-secondary)', bg: 'rgba(255,255,255,0.04)' },
    warning: { fg: 'var(--color-warning-text)', bg: 'rgba(251,191,36,0.1)' },
    error: { fg: 'var(--color-error-text)', bg: 'rgba(239,68,68,0.12)' },
    critical: { fg: 'white', bg: 'rgba(239,68,68,0.4)' },
  };
  const c = colors[severity];
  return (
    <span
      style={{
        fontSize: '9px',
        fontWeight: 500,
        color: c.fg,
        background: c.bg,
        padding: '2px 6px',
        borderRadius: '3px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {severity}
    </span>
  );
}

function Term({ children }: { children: React.ReactNode }) {
  return (
    <dt style={{ color: 'var(--color-text-tertiary)' }}>{children}</dt>
  );
}

function Detail({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <dd style={{ margin: 0, color: 'var(--color-text-secondary)', ...style }}>{children}</dd>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        fontWeight: 500,
        padding: '8px',
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <td style={{ padding: '8px', verticalAlign: 'top', ...style }}>{children}</td>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onChange,
  disabled,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (next: number) => void;
  disabled: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <span style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>
        {page} / {totalPages} ページ(全 {total} 件)
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ minWidth: 80 }}>
          <Button
            variant="secondary"
            disabled={disabled || page <= 1}
            onClick={() => onChange(page - 1)}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            前へ
          </Button>
        </div>
        <div style={{ minWidth: 80 }}>
          <Button
            variant="secondary"
            disabled={disabled || page >= totalPages}
            onClick={() => onChange(page + 1)}
            style={{ padding: '6px 10px', fontSize: '11px' }}
          >
            次へ
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ja-JP', { dateStyle: 'short', timeStyle: 'medium' });
}
